/**
 * WipeScreen
 *
 * Self-destructing route. Open https://nullumclient.netlify.app/wipe
 * to wipe localStorage + IndexedDB + ServiceWorkers + Cache for this
 * browser, then redirect to /welcome.
 *
 * Used for development cleanup. Should be removed (or hidden) before
 * public launch.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function WipeScreen() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [done, setDone] = useState(false);

  function emit(msg, level = 'ok') {
    setLogs((prev) => [...prev, { msg, level }]);
  }

  useEffect(() => {
    let alive = true;
    (async () => {
      // 1. localStorage + sessionStorage
      try {
        const lsCount = localStorage.length;
        const ssCount = sessionStorage.length;
        localStorage.clear();
        sessionStorage.clear();
        if (alive) emit(`localStorage (${lsCount}) + sessionStorage (${ssCount}) cleared`);
      } catch (e) {
        if (alive) emit(`localStorage error: ${e.message}`, 'err');
      }

      // 2. IndexedDB
      try {
        let dbs = [];
        if (indexedDB.databases) {
          dbs = await indexedDB.databases();
        } else {
          dbs = [{ name: 'nullum' }];
        }
        for (const dbInfo of dbs) {
          if (!dbInfo.name) continue;
          await new Promise((res) => {
            const req = indexedDB.deleteDatabase(dbInfo.name);
            req.onsuccess = () => { if (alive) emit(`IndexedDB "${dbInfo.name}" dropped`); res(); };
            req.onerror = () => { if (alive) emit(`IndexedDB "${dbInfo.name}" error`, 'err'); res(); };
            req.onblocked = () => { if (alive) emit(`IndexedDB "${dbInfo.name}" blocked`, 'warn'); res(); };
          });
        }
        if (alive && dbs.length === 0) emit('no IndexedDB to clear');
      } catch (e) {
        if (alive) emit(`IndexedDB error: ${e.message}`, 'err');
      }

      // 3. Service Workers
      try {
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          for (const r of regs) {
            await r.unregister();
            if (alive) emit(`ServiceWorker "${r.scope}" unregistered`);
          }
          if (alive && regs.length === 0) emit('no SW to unregister');
        }
      } catch (e) {
        if (alive) emit(`SW error: ${e.message}`, 'err');
      }

      // 4. Cache Storage
      try {
        if ('caches' in window) {
          const names = await caches.keys();
          for (const n of names) {
            await caches.delete(n);
            if (alive) emit(`cache "${n}" deleted`);
          }
          if (alive && names.length === 0) emit('no caches');
        }
      } catch (e) {
        if (alive) emit(`Cache error: ${e.message}`, 'err');
      }

      if (alive) {
        emit('');
        emit('=== WIPE COMPLETATO ===', 'ok');
        setDone(true);
      }

      // Auto-redirect after 3 seconds
      setTimeout(() => {
        if (alive) {
          // Hard reload to force the browser to drop any stale JS module
          // referencing the old store etc.
          window.location.replace('/welcome');
        }
      }, 3000);
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0a0a',
      color: '#00ff66',
      fontFamily: 'monospace',
      padding: 40,
      fontSize: 13,
      lineHeight: 1.6,
    }}>
      <h1 style={{ color: '#ffaa00', fontSize: 22, margin: '0 0 16px' }}>
        // NULLUM :: WIPE_BROWSER
      </h1>
      <p style={{ color: '#888' }}>
        cancellazione di localStorage, IndexedDB, ServiceWorker, cache...
      </p>
      <pre style={{
        background: '#000',
        border: '1px solid #006633',
        padding: 12,
        marginTop: 16,
        whiteSpace: 'pre-wrap',
        maxHeight: '60vh',
        overflowY: 'auto',
      }}>
        {logs.map((l, i) => (
          <div key={i} style={{
            color: l.level === 'err' ? '#ff4444' :
                   l.level === 'warn' ? '#ffaa00' : '#00ff66',
          }}>
            {l.level === 'err' ? '✕ ' : l.level === 'warn' ? '! ' : l.msg ? '✓ ' : ''}{l.msg}
          </div>
        ))}
      </pre>
      {done && (
        <p style={{ color: '#ffaa00', marginTop: 16 }}>
          reindirizzamento a /welcome tra 3 secondi...
        </p>
      )}
    </div>
  );
}
